'use client';

import { lazy, Suspense } from 'react';
import { useLoadingStore } from 'store/loadingStore';
import Loader from 'atoms/Loader';
import Toast from 'molecules/Toast';
import useGlobalToastStore, { useGlobalAlertStore, useGlobalProcessedRecordsStore } from 'store/globalToastStore';
import AlertModal from 'molecules/AlertModal';
import Text from 'atoms/Text';
import ProgressedRecordsLoader from "components/ProgressedRecordsLoader"
import { useEffect } from 'hooks/useEffect';
import { signOut, useSession } from 'next-auth/react';
import dialogActionStore from 'store/dialogActionStore';
import useContextMenuActionStore from 'store/contextMenuActionStore';
import useAllocationHooks from 'hooks/useAllocationHooks';
import useShortlistedHooks from 'hooks/useShortlistedHooks';
import AllocationTooltipContent from 'molecules/AllocationTooltipContent';
import AllocationErrorTooltip from 'molecules/AllocationErrorTooltip';
import useAvlReportToolTip from 'store/avlReportTooltipStore';

const EditAllocation = lazy(() => import('organisms/allocations/EditAllocation'));
const TakeReason = lazy(() => import('organisms/UnAllocate'));
const AddAllocationDrawerWrapper = lazy(() => import('components/AddAllocationDrawerWrapper'));
const SoftBlock = lazy(() => import('organisms/SoftBlock'));
const CreateSuggestionModal = lazy(() => import('organisms/createSuggestionModal'));


export default function LoaderWrapper() {
    const { loading } = useLoadingStore();
    const { toastData, setToastData } = useGlobalToastStore();
    const { alertData, setAlertData } = useGlobalAlertStore();
    const { progressData } = useGlobalProcessedRecordsStore();
    const { dailogType, emptyDialogActionData, dialogActionData } = dialogActionStore();
    const { setAction } = useContextMenuActionStore();
    const { onUnallocateConfirm } = useAllocationHooks();
    const { onEditSoftBlock, onEditSuggestion } = useShortlistedHooks();
    const { tooltip, errorTooltip, setErrorTooltip } = useAvlReportToolTip();

    const session = useSession();

    useEffect(() => {
        const hasEmail = session?.data?.user?.email;

        if ((!hasEmail || session?.data?.user?.isFreshLogin !== 'yes') && !window.location.pathname?.includes('login')) {
            signOut({
                redirect: true,
                callbackUrl: '/login'
            });
        }
    }, [])
    
    useEffect(() => {
        if (!errorTooltip.show) return;
        const hideAllocationTooltips = () => {
            setErrorTooltip({ show: false, data: {}, pos: { x: 0, y: 0 } });
        };

        window.addEventListener('scroll', hideAllocationTooltips, true);

        return () => {
            window.removeEventListener('scroll', hideAllocationTooltips, true);
        };
    }, [errorTooltip.show, setErrorTooltip]);

    return (
        <>
            {
                progressData?.show && <ProgressedRecordsLoader processedRecords={progressData?.processedRecords} />
            }

            {
                <Toast
                    show={toastData.show}
                    variant={toastData.type || toastData.variant}
                    message={toastData.message}
                    onClose={() => setToastData({ show: false })}
                    closeLabel={toastData.closeLabel || 'Close'}
                >
                    {
                        toastData.messageArr?.length
                            ? <ul className='list-disc text-sm p2-3 pb-5 px-9'>
                                {
                                    toastData.messageArr.map((msg, idx) => (
                                        <li key={idx}>{msg}</li>
                                    ))
                                }
                            </ul>
                            : null
                    }

                </Toast>
            }

            {tooltip.show && (
                <AllocationTooltipContent tooltip={tooltip} />
            )}
            {errorTooltip.show && (
                <AllocationErrorTooltip tooltip={errorTooltip} />
            )}

            {alertData.show && <AlertModal
                show={alertData.show}
                title={alertData.title}
                description={typeof alertData.message == 'string' ? <Text>{alertData.message}</Text> : alertData.message}
                cancelLabel={alertData.cancelLabel ?? "No"}
                saveLabel={alertData.saveLabel ?? "Yes"}
                onClose={() => setAlertData({ show: false })}
                onCancel={() => setAlertData({ show: false })}
                showCalcelButton={alertData.cancelLabel ? true : false}
                onSave={() => {
                    if (alertData.onSave) {
                        alertData.onSave();
                    } else {
                        setAlertData({ show: false });
                    }
                }}
            />}
            {
                ['edit-schedule', 'edit-proposal'].includes(dailogType) && <Suspense
                    fallback={<Loader type="dialog" />}
                >
                    <EditAllocation
                        onSave={({ updatedData: data, dailogType }) => {
                            setAction({
                                action: ['edit-proposal'].includes(dailogType)
                                    ? 'saveEditShortlistedSchedule'
                                    : 'saveEditSchedule',
                                data
                            });
                        }}
                    />
                </Suspense>
            }

            {
                dailogType === 'edit-softblock' && (
                    <Suspense
                        fallback={<Loader type="dialog" />}
                    >
                        <SoftBlock
                            isArticle={dialogActionData?.isArticle}
                            title='Extend soft-block period'
                            show={true}
                            onClose={() => {
                                emptyDialogActionData();
                            }}
                            onSave={async (data) => {
                                const { success, data: updatedData } = await onEditSoftBlock(data, dialogActionData);
                                if (success) {
                                    setAction({
                                        action: 'saveEditShortlistedSchedule',
                                        data: updatedData
                                    });
                                }
                            }}
                            endDate={dialogActionData?.endDate}
                            startDate={dialogActionData?.startDate}
                            extendReason={dialogActionData.extendReason}
                            startDateDisabled={false}
                            scheduleEndDateDisabled={false}
                            scheduleStartDateDisabled={false}
                            housrPerWeekDisabled={false}
                            scheduleStartDate={dialogActionData?.scheduleStartDate}
                            scheduleEndDate={dialogActionData?.scheduleEndDate}
                            weeklyHours={dialogActionData.hoursPerWeek}
                            demandData={{
                                demandStartDate: dialogActionData?.demandData?.demandStartDate,
                                demandEndDate: dialogActionData?.demandData?.demandEndDate,
                                weeklyHours: dialogActionData?.demandData?.weeklyHours
                            }}
                            isEdit={true}
                        />
                    </Suspense>

                )
            }

            {
                dailogType === 'edit-suggestion' && (
                    <Suspense
                        fallback={<Loader type="dialog" />}
                    >
                        <CreateSuggestionModal
                            isArticle={dialogActionData?.isArticle}
                            show={true}
                            onClose={() => {
                                emptyDialogActionData();
                            }}
                            onSave={async (data) => {
                                const { success, data: updatedData } = await onEditSuggestion(data, dialogActionData);
                                if (success) {
                                    setAction({
                                        action: 'saveEditShortlistedSchedule',
                                        data: updatedData
                                    });
                                }

                            }}
                            defaultValues={{
                                createdAt: dialogActionData?.createdAt,
                                startDate: dialogActionData?.startDate,
                                endDate: dialogActionData?.endDate
                            }}
                            isEdit={true}
                        />
                    </Suspense>

                )
            }

            {
                dailogType === 'add-schedule' && (
                    <Suspense
                        fallback={<Loader type="dialog" />}
                    >
                        <AddAllocationDrawerWrapper
                            onHideAllocationsDrawer={(data) => {
                                if (['allocation-tab', 'shortlist-tab'].includes(data)) {
                                    setAction({ action: 'saveAddSchedule', data });
                                } else {
                                    emptyDialogActionData()
                                }
                            }}
                            setPaginationValue={() => { }}
                            setToastData={setToastData}
                        />
                    </Suspense>

                )
            }

            {
                dailogType === 'take-reason' && <Suspense
                    fallback={<Loader type="dialog" />}
                >
                    <TakeReason
                        show={true}
                        onClose={() => {
                            emptyDialogActionData();
                        }}
                        onSave={(data) => {
                            onUnallocateConfirm({
                                reason: data.unallocateReason,
                                resourceId: dialogActionData.resourceId,
                                projectId: dialogActionData.projectId,
                                onSuccess: dialogActionData.onSuccess
                            });
                            emptyDialogActionData();
                        }}
                    />
                </Suspense>
            }

            {
                loading ? <Loader type="fullPage" /> : null
            }
        </>
    );
}
